import { useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem, Select } from "@mui/material";

import moment from "moment";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { koKR } from "@mui/x-date-pickers/locales";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { TrainingTypeArray } from "../../types/user";
import { usePatchStudent, useStudent } from "../../api/studentApi";
import {
  usePatchTrainingProgress,
  useTrainingProgress,
} from "../../api/progressApi";
import { translator } from "../../utils/translator";

import "./report.scss";
import { getTotalTime } from "../../utils/periodUtils";

const TimePickerLocaleText = {
  fieldHoursPlaceholder: () => "시",
  fieldMinutesPlaceholder: () => "분",
  fieldMeridiemPlaceholder: () => "am/pm",
};

type Period = {
  date: moment.Moment;
  start_time: moment.Moment;
  end_time: moment.Moment;
};

const getSortedPeriod = (period: Period[]) => {
  const copyPeriod = [...period];
  copyPeriod.sort((a, b) => {
    const dateA = a.date;
    const dateB = b.date;
    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;
    const startTimeA = a.start_time;
    const startTimeB = b.start_time;
    if (startTimeA < startTimeB) return -1;
    if (startTimeA > startTimeB) return 1;
    const endTimeA = a.end_time;
    const endTimeB = b.end_time;
    if (endTimeA < endTimeB) return -1;
    if (endTimeA > endTimeB) return 1;
    return 0;
  });
  return copyPeriod;
};

const Report = () => {
  const { data: student } = useStudent();
  const { data: trainingProgress } = useTrainingProgress();
  const { mutateAsync: patchTrainingProgress } = usePatchTrainingProgress();
  const { mutateAsync: patchStudent } = usePatchStudent();

  const [editData, setEditData] = useState<
    Record<string, any> & { period?: Array<Period> }
  >({});

  const getTranslatedPeriod = useCallback(
    (period: Array<Record<string, string>>) => {
      if (!period) return [];
      return period.map((p: any) => ({
        date: moment(p.date),
        start_time: moment(p.start_time, "hh:mm a"),
        end_time: moment(p.end_time, "hh:mm a"),
      }));
    },
    []
  );

  useEffect(() => {
    if (!trainingProgress) return;
    setEditData({
      ...trainingProgress.training_progress,
      period: getTranslatedPeriod(trainingProgress.training_progress.period),
    });
  }, [trainingProgress, getTranslatedPeriod]);

  const saveData = useCallback((k: string, v: any) => {
    setEditData((prev) => ({ ...prev, [k]: v }));
  }, []);

  const savePeriod = useCallback((idx: number, period: any) => {
    setEditData((prev) => ({
      ...prev,
      period: [
        ...(prev.period?.slice(0, idx) || []),
        period,
        ...(prev.period?.slice(idx + 1) || []),
      ],
    }));
  }, []);

  const totalTime = useMemo(() => {
    if (!editData.period) return 0;
    return getTotalTime(editData.period);
  }, [editData.period]);

  const submit = useCallback(async () => {
    // validate
    if (editData.period) {
      const sortedPeriod = getSortedPeriod(editData.period);

      for (let i = 0; i < sortedPeriod.length - 1; i++) {
        const currentPeriod = sortedPeriod[i];
        const nextPeriod = sortedPeriod[i + 1];
        if (
          !currentPeriod.date ||
          !currentPeriod.start_time ||
          !currentPeriod.end_time
        ) {
          continue;
        }
        if (
          currentPeriod.date.format("YYYY-MM-DD") ===
          nextPeriod.date.format("YYYY-MM-DD")
        ) {
          const currentEndTime = currentPeriod.end_time;
          const nextStartTime = nextPeriod.start_time;

          if (currentEndTime > nextStartTime) {
            alert("겹치는 기간이 있습니다.");
            return;
          }
        }
      }

      for (let i = 0; i < editData.period.length; i++) {
        const p = editData.period[i] as any;
        if (!p.date && !p.start_time && !p.end_time) {
          continue;
        }

        if (!p.date || !p.start_time || !p.end_time) {
          alert("현장실습 날짜, 시작 시간, 종료 시간을 모두 입력해주세요.");
          return;
        }

        if (p.end_time <= p.start_time) {
          alert("종료 시간이 시작 시간보다 빠릅니다.");
          return;
        }
      }
    }

    if (editData.score) {
      if (!/^\d+$/.test(editData.score)) {
        alert("점수는 정수로 입력해주세요.");
        return;
      }

      const scoreNum = parseInt(editData.score, 10);

      if (scoreNum < 0 || scoreNum > 100) {
        alert("점수는 0부터 100 사이의 숫자여야 합니다.");
        return;
      }
    }

    // submit
    const data: Record<string, any> = {};
    const generalFields = [
      "sports",
      "corp",
      "corp_address",
      "instructor_name",
      "instructor_phone",
    ];

    generalFields.forEach((field) => {
      if (editData[field] !== trainingProgress.training_progress[field])
        data[field] = editData[field];
    });

    if (editData.score !== trainingProgress.training_progress.score)
      data.score = parseInt(editData.score, 10);
    if (editData.period !== trainingProgress.training_progress.period) {
      data.period = getSortedPeriod(
        editData.period
          ?.filter((p: any) => p.date && p.start_time && p.end_time)
          .map((p: any) => ({
            date: p.date.format("YYYY-MM-DD"),
            start_time: p.start_time.format("HH:mm"),
            end_time: p.end_time.format("HH:mm"),
          })) || []
      );
      data.training_time = totalTime;
    }

    await patchTrainingProgress(data);

    if (
      editData["training_type"] &&
      editData["training_type"] !== student.student.training_type
    ) {
      await patchStudent({
        training_type: editData["training_type"],
      });
    }
    alert("성공적으로 저장되었습니다.");
  }, [editData, patchTrainingProgress, trainingProgress, totalTime, student]);

  if (!Object.keys(editData).length || !student) return <p>로딩 중...</p>;

  return (
    <div className="fullWidth">
      <button onClick={submit} className="moveButton">
        저장하기
      </button>
      <div id="report-binary-grid" className="wideContent">
        <>
          <p>연수기관 이름</p>
          <p>한양대학교</p>
        </>
        <>
          <p>현장실습 종목</p>
          <p>{student.student.sport_type}</p>
        </>
        <>
          <p>현장실습 타입</p>
          <div>
            <Select
              variant="standard"
              value={editData["training_type"] || student.student.training_type}
              onChange={(e) => {
                e.stopPropagation();
                if (
                  student.student.training_type &&
                  !window.confirm(
                    "현장실습 타입을 변경하면 업로드한 현장실습 문서 채점이 초기화됩니다. 정말 수정하시겠습니까?"
                  )
                ) {
                  return;
                }
                saveData("training_type", e.target.value);
              }}
            >
              {TrainingTypeArray.map((type) => ({
                label: translator[type],
                value: type,
              })).map((option) => (
                <MenuItem value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </div>
          {/* <p>
            {translator[`training_${student.student.training_type}`]
              .split("현장실습 - ")
              .pop()}
          </p> */}
        </>
        <>
          <p>현장실습 기관이름</p>
          <div>
            <input
              type="text"
              autoComplete="new-password"
              value={editData.corp}
              onChange={(e) => saveData("corp", e.target.value.slice(0, 50))}
            />
          </div>
        </>
        <>
          <p>현장실습 기관주소</p>
          <div>
            <input
              type="text"
              autoComplete="new-password"
              value={editData.corp_address}
              onChange={(e) =>
                saveData("corp_address", e.target.value.slice(0, 100))
              }
            />
          </div>
        </>
        <>
          <p>현장실습 지도자 이름</p>
          <div>
            <input
              type="text"
              autoComplete="new-password"
              value={editData.instructor_name}
              onChange={(e) =>
                saveData("instructor_name", e.target.value.slice(0, 20))
              }
            />
          </div>
        </>
        <>
          <p>현장실습 지도자 연락처</p>
          <div>
            <input
              type="text"
              autoComplete="new-password"
              value={editData.instructor_phone}
              onChange={(e) =>
                saveData("instructor_phone", e.target.value.slice(0, 50))
              }
            />
          </div>
        </>
        <>
          <p>현장실습 기간</p>
          <div style={{ paddingTop: "10px", paddingBottom: "10px" }}>
            <div className="period-wrapper">
              <div>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  localeText={
                    koKR.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                >
                  {editData.period?.length === 0 ? (
                    <div key="emptyPeriod" className="period">
                      <DatePicker
                        value={undefined}
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: {
                            placeholder: `현장실습 날짜 1`,
                            variant: "standard",
                          },
                        }}
                        disableFuture={true}
                        onChange={(value) => savePeriod(0, { date: value })}
                      />
                      <TimePicker
                        value={undefined}
                        localeText={TimePickerLocaleText}
                        timeSteps={{ minutes: 15 }}
                        ampm={false}
                        format="HH:mm"
                        slotProps={{
                          textField: {
                            placeholder: `시작 시간 (시계 클릭)`,
                            variant: "standard",
                          },
                        }}
                        closeOnSelect={false}
                        onChange={(value) =>
                          savePeriod(0, {
                            start_time: value,
                          })
                        }
                      />
                      <TimePicker
                        value={undefined}
                        localeText={TimePickerLocaleText}
                        timeSteps={{ minutes: 15 }}
                        ampm={false}
                        format="HH:mm"
                        slotProps={{
                          textField: {
                            placeholder: `종료 시간 (시계 클릭)`,
                            variant: "standard",
                          },
                        }}
                        closeOnSelect={false}
                        onChange={(value) =>
                          savePeriod(0, {
                            end_time: value,
                          })
                        }
                      />
                    </div>
                  ) : (
                    editData.period?.map((p: any, idx: number) => (
                      <div className="period-time-delete">
                        <div key={`period${idx}`} className="period">
                          <DatePicker
                            value={
                              p.date
                                ? typeof p.date === "string"
                                  ? moment(p.date)
                                  : p.date
                                : undefined
                            }
                            format="YYYY-MM-DD"
                            slotProps={{
                              textField: {
                                placeholder: `현장실습 날짜 ${idx + 1}`,
                                variant: "standard",
                              },
                            }}
                            onChange={(value) =>
                              savePeriod(idx, {
                                ...p,
                                date: value,
                              })
                            }
                          />
                          <TimePicker
                            value={
                              p.start_time
                                ? typeof p.start_time === "string"
                                  ? moment(p.start_time, "HH:mm")
                                  : p.start_time
                                : undefined
                            }
                            timeSteps={{ minutes: 15 }}
                            localeText={TimePickerLocaleText}
                            ampm={false}
                            format="HH:mm"
                            slotProps={{
                              textField: {
                                placeholder: `시작 시간 (시계 클릭)`,
                                variant: "standard",
                              },
                            }}
                            closeOnSelect={false}
                            onChange={(value) =>
                              savePeriod(idx, {
                                ...p,
                                start_time: value,
                              })
                            }
                          />
                          <TimePicker
                            value={
                              p.end_time
                                ? typeof p.end_time === "string"
                                  ? moment(p.end_time, "HH:mm")
                                  : p.end_time
                                : undefined
                            }
                            localeText={TimePickerLocaleText}
                            timeSteps={{ minutes: 15 }}
                            ampm={false}
                            format="HH:mm"
                            slotProps={{
                              textField: {
                                placeholder: `종료 시간 (시계 클릭)`,
                                variant: "standard",
                              },
                            }}
                            closeOnSelect={false}
                            onChange={(value) =>
                              savePeriod(idx, {
                                ...p,
                                end_time: value,
                              })
                            }
                          />
                        </div>
                        {p.end_time &&
                          p.start_time &&
                          (p.end_time > p.start_time ? (
                            <span className="time">
                              {(p.end_time - p.start_time) / 1000 / 60}분
                            </span>
                          ) : (
                            <p
                              style={{
                                color: "var(--red-300)",
                                marginTop: "10px",
                              }}
                            >
                              종료 시간이 시작 시간보다 빠릅니다.
                            </p>
                          ))}
                        <button
                          className="period-delete-button"
                          onClick={() => {
                            setEditData((prev) => ({
                              ...prev,
                              period: [
                                ...(prev.period?.slice(0, idx) || []),
                                ...(prev.period?.slice(idx + 1) || []),
                              ],
                            }));
                          }}
                        >
                          ❌
                        </button>
                      </div>
                    ))
                  )}
                </LocalizationProvider>
              </div>
              <button
                onClick={() =>
                  setEditData((prev) => ({
                    ...prev,
                    period: [
                      ...(prev.period || []),
                      { date: "", start_time: "", end_time: "" },
                    ],
                  }))
                }
              >
                추가
              </button>
            </div>
          </div>
        </>
        <>
          <p>총 현장실습 시간</p>
          <p>{totalTime}분</p>
        </>
        <>
          <p>총 점수</p>
          <div>
            <input
              type="tel"
              autoComplete="new-password"
              value={editData.score}
              onChange={(e) => saveData("score", e.target.value.slice(0, 3))}
            />
          </div>
        </>
      </div>
    </div>
  );
};

export default Report;
